/**
 * FX Service Provider Constants
 *
 * Centralized provider configurations for foreign exchange services.
 * These constants are used for displaying FX service providers in the admin interface.
 */

/**
 * Service Provider Identifiers
 */
export enum FXServiceProvider {
  XENDIT = 'xendit',
  UOB = 'uob',
  OCBC = 'ocbc',
  HSBC = 'hsbc',
  HSBC_GD = 'hsbcgd', // Global Disbursement (China)
  CITI_WL = 'citiwl', // WorldLink (India)
  MOCK = 'mock', // Testing only
}

/**
 * Display labels for FX service providers (for frontend dropdowns)
 */
export const FX_PROVIDER_LABELS: Record<string, string> = {
  xendit: 'Xendit',
  uob: 'UOB',
  ocbc: 'OCBC',
  hsbc: 'HSBC',
  hsbcgd: 'HSBC Global Disbursement',
  citiwl: 'Citi WorldLink',
  mock: 'Mock Provider (Testing)',
};

/**
 * Country codes for FX operations
 */
export enum FXCountryCode {
  PH = 'ph',
  MY = 'my',
  ID = 'id',
  CN = 'cn',
  IN = 'in',
}

/**
 * Country provider preferences (priority order)
 * First provider in array has highest priority
 *
 * Environment-aware configuration:
 * - Staging: PH/MY/ID use MOCK and XENDIT for testing
 * - Production: PH/MY/ID use production providers (XENDIT/HSBC/UOB)
 */
const isProduction = process.env.NODE_ENV === 'production';

export const COUNTRY_PROVIDER_PREFERENCES: Record<FXCountryCode, FXServiceProvider[]> = {
  [FXCountryCode.PH]: isProduction ? [FXServiceProvider.XENDIT] : [FXServiceProvider.MOCK, FXServiceProvider.XENDIT],
  [FXCountryCode.MY]: isProduction ? [FXServiceProvider.HSBC] : [FXServiceProvider.MOCK, FXServiceProvider.XENDIT],
  [FXCountryCode.ID]: isProduction ? [FXServiceProvider.UOB] : [FXServiceProvider.MOCK, FXServiceProvider.XENDIT],
  [FXCountryCode.CN]: [FXServiceProvider.HSBC],
  [FXCountryCode.IN]: [FXServiceProvider.HSBC],
};

/**
 * Generate default FX service provider array from constants
 * Excludes mock provider for display purposes
 */
export const getDefaultFxServiceProviders = () => {
  return Object.entries(COUNTRY_PROVIDER_PREFERENCES).map(([country, providers]) => ({
    country,
    providers: providers.filter((p) => p !== FXServiceProvider.MOCK),
  }));
};

/**
 * Helper function to format FX service provider display
 * Formats provider data into human-readable string
 */
export const formatFxProviderDisplay = (item: any): string => {
  if (typeof item !== 'object') return item;
  const providerLabels = item.providers.map((p: string) => FX_PROVIDER_LABELS[p] || p);
  return `${item.country.toUpperCase()}: ${providerLabels.join(', ')}`;
};
